// @flow
// Functions for grouping mapsources
import type { GroupingType, PropsType, OrderingType } from './mapsources'

/**
 * Single group.
 * @param props
 * @param sortBy Field to sort by
 * @returns {Array}
 */
export const getGroupsForTypeNone = (props, sortBy: string) => {
    const layers = []
    const tables = []
    props.mapsources.mapsources.forEach((mapsource) => {
        if (mapsource.type === 'mapserver') {
            mapsource.layers.forEach(l => l.layer_type === 'table' ? tables.push(l) : layers.push(l))
        } else if (mapsource.type === 'arcgisrest') {
            layers.push(mapsource)
        } else if (mapsource.type === 'timeseries') {
            layers.push(mapsource)
        }
    })
    if (sortBy) {
        layers.sort((l1, l2) => l1[sortBy] < l2[sortBy])
        tables.sort((t1, t2) => t1[sortBy] < t2[sortBy])
    }
    const groups = []
    if (layers.length) {
        groups.push({
            layers,
            name: 'Layers',
        })
    }
    if (tables.length) {
        groups.push({
            layers: tables,
            name: 'Tables',
        })
    }
    return groups
}

export const getGroupsForTypeLayer = (props) => {
    const groups = []
    const types = ['Point', 'Line', 'Polygon', 'Service', 'Raster', 'Table', 'Timeseries']
    types.forEach((layerType) => {
        const layerGroup = {
            layers: [],
            name: layerType,
        }
        props.mapsources.mapsources.forEach((mapsource) => {
            if (mapsource.type === 'mapserver') {
                mapsource.layers.forEach((layer) => {
                    if (layer.layer_type.toLowerCase().indexOf(layerType.toLowerCase()) >= 0) {
                        layerGroup.layers.push(layer)
                    }
                })
            } else if (mapsource.type === 'arcgisrest' && layerType === 'Service') {
                layerGroup.layers.push(mapsource)
            } else if (mapsource.type === 'timeseries' && layerType === 'Timeseries') {
                layerGroup.layers.push(mapsource)
            }
        })
        if (layerGroup.layers.length) {
            groups.push(layerGroup)
        }
    })
    return groups
}

export const getGroupsForTypeTag = (props) => {
    const groups = []
    // Get all the tags
    const mapserverSources = props.mapsources.mapsources.filter(m => m.type === 'mapserver')
    const servicesTag = 'Services'
    const alltags = [servicesTag]
    mapserverSources.forEach(m => m.layers.forEach((l) => {
        if (l.tags) alltags.push(...l.tags)
    }))
    const processed = []
    const tags = [...new Set(alltags), undefined]
    tags.forEach((tag) => {
        const layerGroup = {
            layers: [],
            name: tag || '???',
        }
        props.mapsources.mapsources.forEach((mapsource) => {
            if (mapsource.type === 'mapserver') {
                mapsource.layers.forEach((layer) => {
                    const layerTags = layer.tags || [undefined]
                    const hasTag = layerTags.indexOf(tag) >= 0
                    if (hasTag && processed.indexOf(layer) < 0) {
                        layerGroup.layers.push(layer)
                        processed.push(layer)
                    }
                })
            } else if (tag === servicesTag) {
                layerGroup.layers.push(mapsource)
            }
        })
        // Append services? Maybe not.
        if (layerGroup.layers.length) {
            groups.push(layerGroup)
        }
    })
    return groups
}

export const getGroupsForTypeCategory = (props) => {
    const groups = []
    const types = ['My Layers', 'Public', 'Timeseries']
    types.forEach((cat) => {
        const layerGroup = {
            layers: [],
            name: cat,
        }
        props.mapsources.mapsources.forEach((mapsource) => {
            if (mapsource.type === 'mapserver' && cat === 'My Layers') {
                mapsource.layers.forEach((layer) => {
                    layerGroup.layers.push(layer)
                })
            } else if (mapsource.type === 'arcgisrest' && cat === 'Public') {
                layerGroup.layers.push(mapsource)
            } else if (mapsource.type === 'timeseries' && cat === 'Timeseries') {
                layerGroup.layers.push(mapsource)
            }
        })
        // Append services? Maybe not.
        if (layerGroup.layers.length) {
            groups.push(layerGroup)
        }
    })
    return groups
}

/**
 * Update the state with the new grouping.
 * @param groupingType
 * @param orderingType 'asc' or 'desc'
 * @param props Either new props or the current
 * @returns {Array}
 */
export const getGrouping = (mask: 'layers' | 'tables', groupingType: GroupingType, orderingType: OrderingType, props: PropsType) => {
    // For now grouping is by mapsource
    let groups = []
    if (groupingType === 'none') {
        groups = getGroupsForTypeNone(props)
    } else if (groupingType === 'category') {
        groups = getGroupsForTypeCategory(props)
    } else if (groupingType === 'layerType') {
        groups = getGroupsForTypeLayer(props)
    } else if (groupingType === 'tag') {
        groups = getGroupsForTypeTag(props)
    } else {
        // Assume sort on metadata
        groups = getGroupsForTypeNone(props, groupingType)
        if (orderingType === 'asc') groups[0].layers.sort()
        else if (orderingType === 'desc') groups[0].layers.sort().reverse()
    }
    // Remove layers that are not supposed to be shown
    if (mask) {
        groups.forEach((group) => {
            group.layers = group.layers.filter(l => (
                (l.layer_type === 'table' && mask === 'tables') ||
                (l.layer_type !== 'table' && mask === 'layers')
            ))
        })
        // Remove empty groups
        groups = groups.filter(g => g.layers.length > 0)
    }

    if (orderingType === 'asc') groups.sort()
    else if (orderingType === 'desc') groups.sort().reverse()
    return groups
}

/**
 * Filter layers in each group based on layer metadata
 * @param groups
 */
export const filterGroups = (groups, filters) => {
    if (filters.length) {
        groups.forEach((group) => {
            group.layers = group.layers.filter((l) => {
                let hasMatch = true
                // If the layer has tags, then search them
                if (l.tags && l.tags.length) {
                    const matchedTags = filters.filter((f) => {
                        return l.tags.filter(t => t === f)
                    })
                    // Every tag must match
                    hasMatch = matchedTags.length === filters.length
                }
                if (hasMatch) {
                    // Filter name using first filter
                    hasMatch = l.name.indexOf(filters[0]) >= 0
                }
                return hasMatch
            })
        })
    }
    return groups
}
